import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { Info, TriangleAlert } from "lucide-react";

import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";

import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionTooltipWrapper } from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { Subscription as SubscriptionData } from "data/models";
import { cancelFutureSubscription } from "data/mutations-common";
import { formatTimestamp } from "utils";
import { resolveIdentityId } from "utils/identity";

interface ScheduledPlanChangeAlertProps {
  subscription: SubscriptionData;
}

export const ScheduledPlanChangeAlert = ({
  subscription,
}: ScheduledPlanChangeAlertProps) => {
  const [open, setOpen] = useState(false);
  const [identity] = useIdentity();
  const identityId = resolveIdentityId(identity);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const active = subscription.activeSubscription!;
  const nextSub = active.nextSub!;

  const { isPermitted: nextSubIsPermitted } = usePermissions({
    permissionSets: [nextSub.plan.product_key],
  });

  const cancelFutureMutation = useMutation({
    mutationFn: () => cancelFutureSubscription(),
    onSuccess: () => {
      queryClient.setQueryData([QueryKeys.subscription, identityId], {
        ...subscription,
        activeSubscription: {
          ...subscription.activeSubscription,
          nextSub: undefined,
        },
      });
      setOpen(false);
      enqueueSnackbar(
        "You successfully cancelled your pending subscription downgrade.",
        { variant: "success" },
      );
    },
  });

  const isDowngrade = active.plan.tier > nextSub.plan.tier;
  const showDowngradeWarning =
    isDowngrade && !nextSubIsPermitted([PermissionsKeys.membersUpdate]);

  return (
    <>
      <Alert variant="info">
        <Info />
        <AlertTitle>Scheduled plan change</AlertTitle>
        <AlertDescription>
          You have a scheduled plan change from {active.plan.display_name} to{" "}
          {nextSub.plan.display_name} that will take effect on the{" "}
          {formatTimestamp(nextSub.startTime * 1000)}. Until then you are still
          able to use all of the features of {active.plan.product_display_name}.
        </AlertDescription>
        <AlertAction>
          <PermissionTooltipWrapper
            permissionKeys={[PermissionsKeys.subscriptionUpdate]}
          >
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                cancelFutureMutation.reset();
                setOpen(true);
              }}
            >
              Cancel scheduled change
            </Button>
          </PermissionTooltipWrapper>
        </AlertAction>
      </Alert>

      {showDowngradeWarning && (
        <Alert variant="warning">
          <TriangleAlert />
          <AlertDescription>
            <strong>WARNING,</strong> All members who are not the{" "}
            <strong>owner</strong> will have their access to Hub disabled at the
            end of the current subscription term on{" "}
            {formatTimestamp(nextSub.startTime * 1000)}.
          </AlertDescription>
        </Alert>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel scheduled plan change?</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel the scheduled plan change? When
              cancelled, your previous subscription of {active.plan.display_name}{" "}
              will renew on {formatTimestamp(active.renewalDate * 1000)}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Go Back
            </Button>
            <Button
              disabled={cancelFutureMutation.isPending}
              onClick={() => cancelFutureMutation.mutate()}
            >
              {cancelFutureMutation.isPending ? (
                <>
                  <Spinner />
                  Cancelling...
                </>
              ) : (
                "Cancel Plan Change"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
